import useLiveSetupFocusPoints from "../../utils/useLiveSetupFocusPoints";
import {usesFirefoxRecordingCompatibility, createFirefoxRecordingCapture} from './firefoxRecordingCompat';
import VodMarkerPicker from './VodMarkerPicker';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./LiveRecordModal.css";

import { db } from "../../Security/firebase-config";
import {
  collection,
  doc,
  Timestamp,
  getDocs,
  getDoc,
  query,
  orderBy,
  limit,
  where,
  writeBatch,
  updateDoc,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  deleteObject,
  getDownloadURL,
} from "firebase/storage";

import { useTeam } from "../../context/TeamContext";
import CustomAlert from "../core-components/CustomAlert";
import DeleteHighlightConfirm from "../core-components/DeleteHighlightConfirm";
import { ensureActiveTeamClaim } from "../../utils/authClaims";
import {
  checkTeamQuotaAllowance,
  confirmTrackedUploadFinalize,
  triggerTeamQuotaRefresh,
} from "../../utils/teamQuota";
import {
  copySessionPlanFocusPointsToMatch,
  linkMatchToCalendarEvent,
  toLocalDateTimeInputs,
} from "../../utils/calendarMatchLink";

import NoteInputBox from "./NoteInputBox";
import NotesDisplay from "./NotesDisplay";
import { toMatchTimeSec } from "./syncTime";

import fixRecordedWebmDuration from "../../utils/fixRecordedWebmDuration";
import indexRecordedWebm from "../../utils/indexRecordedWebm";

import FocusPoints from "./FocusPoints";
import CalendarMatchLinker from "../planning/CalendarMatchLinker";
import {readSetupSelection} from "../../utils/eventSetupStore";
import useEventSetupLifecycle from "../planning/useEventSetupLifecycle";
import {
  createLiveLobby,
  commitLiveLobbySegment,
  endLiveLobby,
  getLiveLobbyShutdownLabel,
  heartbeatLiveLobby,
} from "../../utils/liveLobby";
import {
  buildMatchReservationKey,
  claimReservedMatch,
  cleanupStaleMatchReservations,
  createMatchReservationSessionId,
  ensureMatchReservation,
  getCurrentLocalDateInputValue,
  getCurrentLocalTimeInputValue,
  isReservedMatchDoc,
  markMatchReservationStarted,
  refreshMatchReservationHeartbeat,
  releaseMatchReservation,
} from "../../utils/matchReservations";


import UploadScoreDialog from "../../core-pages/team-stats/UploadScoreDialog";

const pad2 = (n) => String(n).padStart(2, "0");

function formatTimeHMS(sec) {
  const s = Math.max(0, Math.floor(Number(sec || 0) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${h}:${pad2(m)}:${pad2(r)}`;
}

/**
 * LiveRecordModal
 * - Player mode: record -> stop -> preview -> mark sync -> save(upload)
 * - Coach mode: same, but can create notes live during recording (match timeline).
 */
export default function LiveRecordModal({ mapId: initialMapId, mapName: initialMapName, initialSetupEventId = "", onClose, onVideoUploaded }) {
  const setupLifecycle = useEventSetupLifecycle();
  const [mapId, setSetupMapId] = useState(initialMapId);
  const [mapName, setSetupMapName] = useState(initialMapName);
  const [setupReady, setSetupReady] = useState(false);
  const LIVE_LOBBY_SEGMENT_DURATION_MS = 4000;
  const { teamId, username } = useTeam();
  const auth = getAuth();

  const [alert, setAlert] = useState(null);

  // Mode selection
  const [mode, setMode] = useState("player"); // 'player' | 'coach'

  // Match selection
  const [matchMode, setMatchMode] = useState("new"); // 'existing' | 'new'
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [resolvedMatchId, setResolvedMatchId] = useState("");
  const [newMatchTitle, setNewMatchTitle] = useState("");
  const [matchDate, setMatchDate] = useState(() => getCurrentLocalDateInputValue());
  const [matchTime, setMatchTime] = useState(() => getCurrentLocalTimeInputValue());
  const [selectedCalendarEventId, setSelectedCalendarEventId] = useState(initialSetupEventId);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState(null);
  const reservationSessionIdRef = useRef(createMatchReservationSessionId());
  const activeReservationRef = useRef(null);

  // Metadata
  const [selectedTags] = useState([]);
  const [selectedComp, setSelectedComp] = useState(["", "", "", "", ""]);
  const [povName, setPovName] = useState("Coach's POV");
  const [existingMatchPovNames, setExistingMatchPovNames] = useState([]);

  


  const povOptions = [
    "Coach's POV",
    "Astra",
    "Breach",
    "Brimstone",
    "Chamber",
    "Clove",
    "Cypher",
    "Deadlock",
    "Fade",
    "Gekko",
    "Harbor",
    "Iso",
    "Jett",
    "KAY/O",
    "Killjoy",
    "Neon",
    "Omen",
    "Phoenix",
    "Raze",
    "Reyna",
    "Sage",
    "Skye",
    "Sova",
    "Tejo",
    "Viper",
    "Vyse",
    "Waylay",
    "Yoru",
  ];

  const firefoxRecording = usesFirefoxRecordingCompatibility();
  const firefoxCaptureRef = useRef(null);

  // Capture settings
  const [includeMic, setIncludeMic] = useState(true);
  const [micDeviceId, setMicDeviceId] = useState("default");
  const [extraAudioDeviceId, setExtraAudioDeviceId] = useState(""); // optional second input device
  const [devices, setDevices] = useState([]);

  // Per-source mute toggles
  const [muteSystem, setMuteSystem] = useState(false);
  const [muteMic, setMuteMic] = useState(false);
  const [muteExtra, setMuteExtra] = useState(false);

  // ✅ Per-source volumes (0..1)
  const [systemVolume, setSystemVolume] = useState(1);
  const [micVolume, setMicVolume] = useState(1);
  const [extraVolume, setExtraVolume] = useState(1);

  // Recording state
  const [isPreparing, setIsPreparing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isStopped, setIsStopped] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uiLocked = isRecording || uploading || (firefoxRecording && isPreparing);
  const [showLiveLobbyConfirm, setShowLiveLobbyConfirm] = useState(false);
  const [creatingLiveLobby, setCreatingLiveLobby] = useState(false);
  const [liveLobbySession, setLiveLobbySession] = useState(null);
  const [liveLobbyFinalizing, setLiveLobbyFinalizing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mixedStreamRef = useRef(null);
  const liveLobbySessionRef = useRef(null);
  const liveLobbySegmentIndexRef = useRef(0);
  const liveLobbyUploadChainRef = useRef(Promise.resolve());
  const liveLobbyStopReasonRef = useRef("manual_end");
  const captureQualityMetaRef = useRef({
    captureWidth: null,
    captureHeight: null,
    captureFrameRate: null,
    recordingMimeType: "",
    recordingMode: "",
  });
  const finalMediaMetaRef = useRef({
    finalVideoWidth: null,
    finalVideoHeight: null,
    durationMs: null,
  });

  const previewVideoRef = useRef(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  // Firestore IDs determined on start (needed for coach notes)
  const [matchId, setMatchId] = useState(null);
  const [videoId, setVideoId] = useState(null);

  // Sync marker (seconds in the recorded file)
  const [syncOffsetSec, setSyncOffsetSec] = useState(null);

  // Require explicit marking of sync
  const [syncMarked, setSyncMarked] = useState(false);

  const [scoreboardCaptureSec, setScoreboardCaptureSec] = useState(null);
  const [scoreboardFrameFile, setScoreboardFrameFile] = useState(null);
  const [openTeamStatsDialog, setOpenTeamStatsDialog] = useState(false);

  // Coach: live clock (match time while recording)
  const startedAtMsRef = useRef(0);
  const [liveNowVideoSec, setLiveNowVideoSec] = useState(0);
  const [liveNowMatchSec, setLiveNowMatchSec] = useState(0);

  // Coach: allow note time override
  const [noteTimeOverrideSec, setNoteTimeOverrideSec] = useState(null);
  const [noteTimeInput, setNoteTimeInput] = useState("");

  // Delete confirm flow
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ✅ Monitoring (audio test)
  const monitorAudioRef = useRef(null);
  const [isMonitoring, setIsMonitoring] = useState(false);

  const canUse = !!teamId && !!mapId && !!auth.currentUser?.uid;
  const reservationIdentity = useMemo(
    () => ({
      uid: auth.currentUser?.uid || "",
      username: username || "Unknown",
      sessionId: reservationSessionIdRef.current,
      surface: "web-live-record",
    }),
    [auth.currentUser?.uid, username]
  );
  const selectedExistingMatch = useMemo(
    () => matches.find((entry) => entry.id === selectedMatchId) || null,
    [matches, selectedMatchId]
  );
  const povUsageTargetMatchId =
    matchMode === "existing" ? selectedMatchId : resolvedMatchId;
  const focusPointTargetMatchId =
    matchId || (matchMode === "existing" ? selectedMatchId : resolvedMatchId);

  // Focuspoints
  const [focusPointsLoading, setFocusPointsLoading] = useState(false);
  const [focusPoints, setFocusPoints] = useState([]);

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [teamStatsDraft, setTeamStatsDraft] = useState(null);
  const [savedTeamStatsMatchId, setSavedTeamStatsMatchId] = useState(null);
  const [savedTeamStatsImagePath, setSavedTeamStatsImagePath] = useState(null);

  useEffect(() => {
    liveLobbySessionRef.current = liveLobbySession;
  }, [liveLobbySession]);


  const handleCalendarEventSelection = (eventId, event) => {
    setSelectedCalendarEventId(eventId || "");
    setSelectedCalendarEvent(event || null);
    setSelectedComp(Array.isArray(event?.comp) ? event.comp : []);

    if (!event) {
      setMatchMode("new");
      return;
    }

    setSetupMapId(event.mapId || "");
    setSetupMapName(event.mapName || event.mapId || "");
    const schedule = toLocalDateTimeInputs(event.startUTC);
    if (schedule.date) setMatchDate(schedule.date);
    if (schedule.time) setMatchTime(schedule.time);
    setNewMatchTitle(String(event.note || event.title || event.tag || "Team match").trim());

    if (event.matchId) {
      setMatchMode("existing");
      setSelectedMatchId(event.matchId);
      return;
    }

    setMatchMode("new");
    setSelectedMatchId("");
  };

  const existingMatchPovNameSet = useMemo(
    () =>
      new Set(
        existingMatchPovNames
          .map((value) => String(value || "").trim().toLowerCase())
          .filter(Boolean)
      ),
    [existingMatchPovNames]
  );

  const buildCloudPovOptionLabel = (option) =>
    existingMatchPovNameSet.has(String(option || "").trim().toLowerCase())
      ? `${option} | Already exists in the Cloud`
      : option;

  const normalizeMatchKey = (title) => title.toLowerCase().replace(/\s+/g, " ").trim();

  const resetTeamStatsModalUiOnly = () => {
    setOpenTeamStatsDialog(false);
  };

  const resetTeamStatsSession = () => {
    setScoreboardCaptureSec(null);
    setScoreboardFrameFile(null);
    setOpenTeamStatsDialog(false);
    setTeamStatsDraft(null);
    setSavedTeamStatsMatchId(null);
    setSavedTeamStatsImagePath(null);
  };

  const openTeamStatsFromPreview = async (capturedFile, capturedSeconds) => {
    const v = previewVideoRef.current;
    if (!v) return;

    // Reopen existing team-stats work if it already exists
    if (scoreboardFrameFile && !capturedFile) {
      setOpenTeamStatsDialog(true);
      return;
    }

    const frameFile = capturedFile || await captureVideoFrameFile(v);
    if (!frameFile) {
      showError("Could not capture the scoreboard frame.");
      return;
    }

    setScoreboardCaptureSec(capturedSeconds ?? Number(v.currentTime.toFixed(3)));
    setScoreboardFrameFile(frameFile);
    setOpenTeamStatsDialog(true);
  };

  const cleanupSavedTeamStatsForLiveRecording = async () => {
    // Saved stats belong to the event, even if this particular POV is cancelled.
    if (selectedCalendarEventId?.startsWith("setup:")) return;
    if (!teamId || !savedTeamStatsMatchId) return;

    try {
      const storage = getStorage();

      if (savedTeamStatsImagePath) {
        try {
          await deleteObject(storageRef(storage, savedTeamStatsImagePath));
        } catch (err) {
          console.warn("Failed to delete saved team-stats image for live recording:", err);
        }
      }

      await deleteDoc(doc(db, "teams", teamId, "matches", savedTeamStatsMatchId));
    } catch (err) {
      console.warn("Failed to clean up saved live-recording team stats:", err);
    } finally {
      resetTeamStatsSession();
    }
  };

  /**
   * Audio graph refs so we can:
   * - adjust volumes while recording
   * - mute/unmute live
   * - optionally monitor the mixed audio (test)
   */
  const audioGraphRef = useRef({
    ctx: null,
    dest: null,
    systemGain: null,
    micGain: null,
    extraGain: null,
    // tracks that are NOT part of the out stream (mic/extra input tracks)
    sourceTracks: [],
  });

  const cleanupAudioGraph = useCallback(() => {
    if (firefoxRecording) firefoxCaptureRef.current?.stop();
    // stop mic/extra tracks
    try {
      const tracks = audioGraphRef.current.sourceTracks || [];
      tracks.forEach((t) => {
        try {
          t.stop();
        } catch {}
      });
    } catch {}
    audioGraphRef.current.sourceTracks = [];

    // stop monitoring
    try {
      if (monitorAudioRef.current) {
        monitorAudioRef.current.pause?.();
        monitorAudioRef.current.srcObject = null;
      }
    } catch {}
    setIsMonitoring(false);

    // close AudioContext
    try {
      if (!firefoxRecording || audioGraphRef.current.ctx?.state !== 'closed') {
        const closing = audioGraphRef.current.ctx?.close?.();
        if (firefoxRecording) closing?.catch?.(() => {});
      }
    } catch {}

    audioGraphRef.current.ctx = null;
    audioGraphRef.current.dest = null;
    audioGraphRef.current.systemGain = null;
    audioGraphRef.current.micGain = null;
    audioGraphRef.current.extraGain = null;
  }, [firefoxRecording]);

  useEffect(() => {
    if (!firefoxRecording) return undefined;
    return () => {firefoxCaptureRef.current?.stop(); cleanupAudioGraph();};
  }, [firefoxRecording, cleanupAudioGraph]);

  const stopRecording = useCallback(() => {
    try {
      mediaRecorderRef.current?.stop?.();

      mixedStreamRef.current?.getTracks?.().forEach((t) => {
        try {
          t.stop();
        } catch {}
      });
      mixedStreamRef.current = null;

      cleanupAudioGraph();
    } catch (e) {
      console.warn("Stop failed:", e);
      cleanupAudioGraph();
    }
  }, [cleanupAudioGraph]);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Keep recent matches live so newly created scrims appear for other users
  useEffect(() => {
    if (!teamId || !mapId) return;

    const matchesRef = collection(db, "teams", teamId, "maps", mapId, "matches");
    const qRef = query(matchesRef, orderBy("createdAt", "desc"), limit(30));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const uniqueById = Array.from(new Map(arr.map((m) => [m.id, m])).values());
        setMatches(uniqueById);
      },
      (e) => {
        console.warn("Failed to subscribe to matches:", e);
      }
    );

    return () => unsub();
  }, [teamId, mapId]);

  useEffect(() => {
    let cancelled = false;

    async function loadExistingMatchPovNames() {
      if (!teamId || !mapId || !povUsageTargetMatchId) {
        setExistingMatchPovNames([]);
        return;
      }

      try {
        const existingPovsSnap = await getDocs(
          query(
            collection(db, "teams", teamId, "maps", mapId, "videos"),
            where("matchId", "==", povUsageTargetMatchId)
          )
        );

        if (cancelled) return;

        setExistingMatchPovNames(
          Array.from(
            new Set(
              existingPovsSnap.docs
                .map((docSnap) => {
                  const nextPovName = docSnap.data()?.povName;
                  return typeof nextPovName === "string" ? nextPovName.trim() : "";
                })
                .filter(Boolean)
            )
          )
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load existing POV names for live record:", error);
        setExistingMatchPovNames([]);
      }
    }

    void loadExistingMatchPovNames();

    return () => {
      cancelled = true;
    };
  }, [mapId, povUsageTargetMatchId, teamId]);

  useEffect(() => {
    if (!teamId || !mapId || !reservationIdentity.uid) return undefined;

    void cleanupStaleMatchReservations({
      db,
      teamId,
      mapId,
    }).catch(() => undefined);

    return undefined;
  }, [mapId, reservationIdentity.uid, teamId]);

  useEffect(() => {
    if (!teamId || !mapId || !reservationIdentity.uid) return undefined;

    let cancelled = false;
    const nextReservationKey =
      matchMode === "new" ? buildMatchReservationKey(newMatchTitle, matchDate, matchTime, selectedCalendarEventId) : "";
    const shouldClaimExistingReservedMatch =
      matchMode === "existing" &&
      !!selectedMatchId &&
      !!selectedExistingMatch &&
      isReservedMatchDoc(selectedExistingMatch);
    const hasSameReservation =
      !!activeReservationRef.current &&
      ((activeReservationRef.current.kind === "new" &&
        activeReservationRef.current.reservationKey === nextReservationKey) ||
        (activeReservationRef.current.kind === "existing" &&
          activeReservationRef.current.matchId === selectedMatchId));

    async function syncReservationSelection() {
      if (!hasSameReservation && activeReservationRef.current) {
        const activeReservation = activeReservationRef.current;
        activeReservationRef.current = null;
        await releaseMatchReservation({
          db,
          teamId,
          mapId,
          matchId: activeReservation.matchId,
          identity: reservationIdentity,
        }).catch(() => undefined);
      }
      if (cancelled) return;

      if (matchMode === "existing") {
        setResolvedMatchId(selectedMatchId || "");

        if (!selectedMatchId || !shouldClaimExistingReservedMatch) {
          return;
        }

        const reservation = await claimReservedMatch({
          db,
          teamId,
          mapId,
          matchId: selectedMatchId,
          identity: reservationIdentity,
        });

        if (cancelled) {
          if (reservation.claimActive) {
            await releaseMatchReservation({
              db,
              teamId,
              mapId,
              matchId: reservation.matchId,
              identity: reservationIdentity,
            }).catch(() => undefined);
          }
          return;
        }

        activeReservationRef.current = reservation.claimActive
          ? {
              kind: "existing",
              matchId: reservation.matchId,
              reservationKey: reservation.reservationKey,
            }
          : null;
        return;
      }

      if (!nextReservationKey) {
        setResolvedMatchId("");
        return;
      }

      const reservation = await ensureMatchReservation({
        eventKey: selectedCalendarEventId,
        db,
        teamId,
        mapId,
        title: newMatchTitle,
        matchDate,
        matchTime,
        tags: selectedTags,
        comp: selectedComp,
        identity: reservationIdentity,
      });

      if (cancelled) {
        if (reservation.claimActive) {
          await releaseMatchReservation({
            db,
            teamId,
            mapId,
            matchId: reservation.matchId,
            identity: reservationIdentity,
          }).catch(() => undefined);
        }
        return;
      }

      activeReservationRef.current = reservation.claimActive
        ? {
            kind: "new",
            matchId: reservation.matchId,
            reservationKey: reservation.reservationKey,
          }
        : null;
      setResolvedMatchId(reservation.matchId);
    }

    void syncReservationSelection();

    return () => {
      cancelled = true;
    };
  }, [
    mapId,
    matchDate,
    matchMode,
    matchTime,
    newMatchTitle,
    selectedCalendarEventId,
    reservationIdentity,
    selectedComp,
    selectedExistingMatch,
    selectedMatchId,
    selectedTags,
    teamId,
  ]);

  useEffect(() => {
    if (!teamId || !mapId || !reservationIdentity.uid) return undefined;

    const heartbeatId = window.setInterval(() => {
      const activeReservation = activeReservationRef.current;
      if (!activeReservation) return;

      void refreshMatchReservationHeartbeat({
        db,
        teamId,
        mapId,
        matchId: activeReservation.matchId,
        identity: reservationIdentity,
      }).catch(() => undefined);
    }, 60_000);

    return () => {
      window.clearInterval(heartbeatId);
    };
  }, [mapId, reservationIdentity, teamId]);

  useEffect(() => {
    return () => {
      const activeReservation = activeReservationRef.current;
      activeReservationRef.current = null;
      if (!activeReservation || !reservationIdentity.uid) return;

      void releaseMatchReservation({
        db,
        teamId,
        mapId,
        matchId: activeReservation.matchId,
        identity: reservationIdentity,
      }).catch(() => undefined);
    };
  }, [mapId, reservationIdentity, teamId]);

  // Enumerate audio input devices (mic/virtual cables)
  useEffect(() => {
    (async () => {
      try {
        // Must request permission first on some browsers to see labels
        const tmp = await navigator.mediaDevices.getUserMedia({ audio: true });
        tmp.getTracks().forEach((t) => t.stop());

        const list = await navigator.mediaDevices.enumerateDevices();
        setDevices(list.filter((d) => d.kind === "audioinput"));
      } catch (e) {
        setDevices([]);
      }
    })();
  }, []);

  // Live clock ticker while recording (for coach notes)
  useEffect(() => {
    if (!isRecording) return;

    const t = setInterval(() => {
      const now = Date.now();
      const videoSec = Math.max(0, (now - startedAtMsRef.current) / 1000);
      setLiveNowVideoSec(videoSec);
      setLiveNowMatchSec(videoSec);
    }, 150);

    return () => clearInterval(t);
  }, [isRecording]);

  useEffect(() => {
    const currentLobbyId = liveLobbySession?.lobby?.id || liveLobbySession?.lobbyId || null;
    if (!teamId || !currentLobbyId) return undefined;

    return onSnapshot(doc(db, "teams", teamId, "liveLobbies", currentLobbyId), (snap) => {
      if (!snap.exists()) return;
      const nextLobby = { id: snap.id, ...snap.data() };
      setLiveLobbySession((current) =>
        current ? { ...current, lobby: nextLobby } : { lobby: nextLobby }
      );
    });
  }, [liveLobbySession?.lobby?.id, liveLobbySession?.lobbyId, teamId]);

  useEffect(() => {
    const currentLobbyId = liveLobbySession?.lobby?.id || liveLobbySession?.lobbyId || null;
    if (!teamId || !currentLobbyId || !isRecording) return undefined;

    let cancelled = false;
    const ping = async () => {
      try {
        const response = await heartbeatLiveLobby({
          teamId,
          lobbyId: currentLobbyId,
          elapsedSec: Number((((Date.now() - startedAtMsRef.current) || 0) / 1000).toFixed(1)),
        });

        if (!cancelled && response?.lobby) {
          setLiveLobbySession((current) =>
            current ? { ...current, lobby: response.lobby } : { lobby: response.lobby }
          );
        }

        if (!cancelled && response?.shouldTerminate && isRecording) {
          liveLobbyStopReasonRef.current = "quota_shutdown";
          stopRecording();
        }
      } catch (heartbeatError) {
        if (!cancelled) {
          console.warn("[LiveRecordModal] Live lobby heartbeat failed:", heartbeatError);
        }
      }
    };

    ping();
    const timer = window.setInterval(ping, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isRecording, liveLobbySession?.lobby?.id, liveLobbySession?.lobbyId, stopRecording, teamId]);

  const bestMimeType = useMemo(() => {
    const candidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
    for (const c of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  }, []);

  const createOptimizedRecorder = (stream) => {
    const candidateOptions = [
      bestMimeType ? {
        mimeType: bestMimeType,
        videoBitsPerSecond: 12_000_000,
        audioBitsPerSecond: 192_000,
      } : null,
      bestMimeType ? { mimeType: bestMimeType } : null,
      {
        videoBitsPerSecond: 12_000_000,
        audioBitsPerSecond: 192_000,
      },
      undefined,
    ].filter((value, index, arr) => {
      if (value == null) return false;
      return arr.findIndex((other) => JSON.stringify(other) === JSON.stringify(value)) === index;
    });

    let lastError = null;
    for (const options of candidateOptions) {
      try {
        return new MediaRecorder(stream, options);
      } catch (error) {
        lastError = error;
      }
    }

    if (!candidateOptions.includes(undefined)) {
      try {
        return new MediaRecorder(stream);
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error("Unable to initialize the recorder.");
  };

  const showError = (message) => setAlert({ message, type: "error" });

  // Apply mute + volume live (gain nodes)
  useEffect(() => {
    const g = audioGraphRef.current.systemGain;
    if (g) g.gain.value = muteSystem ? 0 : Number(systemVolume || 0);
  }, [muteSystem, systemVolume]);

  useEffect(() => {
    const g = audioGraphRef.current.micGain;
    if (g) g.gain.value = muteMic ? 0 : Number(micVolume || 0);
  }, [muteMic, micVolume]);

  useEffect(() => {
    const g = audioGraphRef.current.extraGain;
    if (g) g.gain.value = muteExtra ? 0 : Number(extraVolume || 0);
  }, [muteExtra, extraVolume]);

  // Build or re-use matchId
  async function resolveMatchIdAndOptionalDoc() {
    if (!setupReady) throw new Error("Save and select your event setup before recording.");
    await readSetupSelection(teamId, selectedCalendarEventId, {requireMap: true, mapId});
    if (matchMode === "existing") {
      if (!selectedMatchId) throw new Error("Select an event and map slot in Setup.");
      const selectedMatch = matches.find((m) => m.id === selectedMatchId);
      return {
        matchIdToUse: selectedMatchId,
        matchTitleToUse: selectedMatch?.title || "Live Recording",
      };
    }

    const desiredTitle = newMatchTitle.trim();
    if (!desiredTitle) throw new Error("Complete and save the event details in Setup.");
    if (!matchDate || !matchTime) {
      throw new Error("Complete and save the event schedule in Setup.");
    }
    if (!resolvedMatchId) {
      throw new Error("Your event is still being prepared. Please wait a moment.");
    }

    return {
      matchIdToUse: resolvedMatchId,
      matchTitleToUse: desiredTitle,
    };
  }

  async function linkCalendarEventForMatch({ matchIdToUse, matchTitleToUse }) {
    const linkedEventId = selectedCalendarEventId || selectedExistingMatch?.calendarEventId || null;
    let allowMapChange = false;
    const plannedMapName = String(selectedCalendarEvent?.mapName || "").trim();
    const currentMapName = String(mapName || "").trim();

    if (
      plannedMapName
      && currentMapName
      && plannedMapName.toLowerCase() !== currentMapName.toLowerCase()
    ) {
      const confirmed = window.confirm(
        `Change the linked event map from ${plannedMapName} to ${currentMapName}? This updates the calendar event and session plan. Map-specific focus points will be marked for review.`,
      );
      if (!confirmed) {
        throw new Error("Map change was cancelled. Recording was not started.");
      }
      allowMapChange = true;
    }

    const playedAt = matchMode === "new" && matchDate && matchTime
      ? new Date(`${matchDate}T${matchTime}:00`)
      : selectedExistingMatch?.matchPlayedAt || new Date();
    const result = await linkMatchToCalendarEvent({
      teamId,
      mapId,
      mapName,
      matchId: matchIdToUse,
      matchTitle: matchTitleToUse,
      playedAt,
      actor: {
        uid: auth.currentUser?.uid || "",
        username: username || auth.currentUser?.email || "Member",
      },
      calendarEventId: linkedEventId,
      allowMapChange,
    });

    setSelectedCalendarEventId(result.event.eventId);
    setSelectedCalendarEvent(result.event);
    return result;
  }

  // Create the "ready scaffold" video doc BEFORE recording so coach notes have matchId available.
  async function createVideoScaffold({
    matchIdToUse,
    matchTitleToUse,
  }) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("You must be signed in.");

    const calendarLink = await linkCalendarEventForMatch({
      matchIdToUse,
      matchTitleToUse,
    });

    const videosColl = collection(db, "teams", teamId, "maps", mapId, "videos");
    const videoRef = doc(videosColl);
    const videoScaffoldData = {
      title: matchTitleToUse || "Live Recording",

      povName,

      videoURL: null,
      storagePath: null,
      sizeBytes: null,
      sourceBytes: null,

      createdBy: username || "Unknown",
      createdByUid: uid,
      createdAt: Timestamp.now(),

      tags: selectedTags,
      comp: selectedComp.filter((a) => a),
      status: "recording",

      matchId: matchIdToUse,
      calendarEventId: calendarLink.event.eventId,
      sessionPlanId: calendarLink.event.sessionPlanId || null,
      syncOffsetSec: 0, // scaffold default; user must explicitly mark later
    };

    const batch = writeBatch(db);
    if (matchMode === "new" && matchIdToUse) {
      batch.set(
        doc(db, "teams", teamId, "maps", mapId, "matches", matchIdToUse),
        {
          title: calendarLink.event.title || matchTitleToUse,
          matchKey: normalizeMatchKey(matchTitleToUse),
          matchPlayedAt: Timestamp.fromDate(new Date(`${matchDate}T${matchTime}:00`)),
          tags: selectedTags,
          comp: selectedComp.filter((a) => a),
        },
        { merge: true }
      );
    }
    batch.set(videoRef, videoScaffoldData);
    await batch.commit();
    await copySessionPlanFocusPointsToMatch({
      teamId,
      mapId,
      matchId: matchIdToUse,
      eventId: calendarLink.event.sessionPlanId || calendarLink.event.eventId,
      focusPoints,
    });

    setMatchId(matchIdToUse);
    setVideoId(videoRef.id);

    return { matchIdToUse, videoIdToUse: videoRef.id };
  }

  // Mix display audio + mic + optional extra input with per-source gain nodes
  async function buildMixedStream(displayStream) {
    const displayVideoTrack = displayStream.getVideoTracks()[0];
    if (!displayVideoTrack) throw new Error("No video track returned from display capture.");

    const audioCtx = firefoxRecording
      ? firefoxCaptureRef.current.audioContext
      : new (window.AudioContext || window.webkitAudioContext)();
    const dest = audioCtx.createMediaStreamDestination();

    audioGraphRef.current.ctx = audioCtx;
    audioGraphRef.current.dest = dest;
    audioGraphRef.current.sourceTracks = [];

    const makeGain = (vol, muted) => {
      const g = audioCtx.createGain();
      g.gain.value = muted ? 0 : Number(vol || 0);
      return g;
    };

    const systemGainNode = makeGain(systemVolume, muteSystem);
    const micGainNode = makeGain(micVolume, muteMic);
    const extraGainNode = makeGain(extraVolume, muteExtra);

    audioGraphRef.current.systemGain = systemGainNode;
    audioGraphRef.current.micGain = micGainNode;
    audioGraphRef.current.extraGain = extraGainNode;

    // 1) display/system audio if present
    const displayAudioTracks = displayStream.getAudioTracks();
    if (displayAudioTracks.length) {
      const systemSource = audioCtx.createMediaStreamSource(displayStream);
      systemSource.connect(systemGainNode).connect(dest);
    }

    // 2) mic
    if (includeMic) {
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: micDeviceId && (!firefoxRecording || micDeviceId !== "default") ? { exact: micDeviceId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      const micTrack = micStream.getAudioTracks()[0];
      if (micTrack) {
        audioGraphRef.current.sourceTracks.push(micTrack);
        const micSource = audioCtx.createMediaStreamSource(micStream);
        micSource.connect(micGainNode).connect(dest);
      }
    }

    // 3) optional extra audio input device
    if (extraAudioDeviceId) {
      const extraStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: extraAudioDeviceId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
        video: false,
      });

      const extraTrack = extraStream.getAudioTracks()[0];
      if (extraTrack) {
        audioGraphRef.current.sourceTracks.push(extraTrack);
        const extraSource = audioCtx.createMediaStreamSource(extraStream);
        extraSource.connect(extraGainNode).connect(dest);
      }
    }

    const out = new MediaStream();
    out.addTrack(displayVideoTrack);

    const mixedAudioTrack = dest.stream.getAudioTracks()[0];
    if (mixedAudioTrack) out.addTrack(mixedAudioTrack);

    return out;
  }

  function prepareFirefoxCapture() {
    const capture = createFirefoxRecordingCapture(navigator.mediaDevices, () => new window.AudioContext());
    firefoxCaptureRef.current = capture;
    return capture;
  }

  async function startRecording(options = {}) {
    let firefoxCreatedVideoId = null;
    try {
      if (firefoxRecording && !options.firefoxCapture && (isPreparing || isRecording || uploading || creatingLiveLobby)) return;
      if (focusPointsLoading) return showError("Wait for the event focus points to finish loading.");
      if (!setupReady) return showError("Save and select your event setup before recording.");
      if (!canUse) return showError("Missing team/map/auth context.");
      if (!navigator.mediaDevices?.getDisplayMedia) {
        return showError("Screen recording is not supported in this browser.");
      }

      setIsPreparing(true);
      setAlert(null);
      liveLobbyStopReasonRef.current = "manual_end";

      const activeLiveLobbySession = options.liveLobbySession || null;
      if (activeLiveLobbySession) {
        setLiveLobbySession(activeLiveLobbySession);
      }

      if (firefoxRecording) {
        const capture = options.firefoxCapture || prepareFirefoxCapture();
        if (!options.firefoxCapture) await capture.start();
        capture.assertActive();
      }

      if (!options.skipScaffold) {
        const resolved = await resolveMatchIdAndOptionalDoc();
        const activeReservation = activeReservationRef.current;
        if (activeReservation?.matchId && activeReservation.matchId === resolved.matchIdToUse) {
          await markMatchReservationStarted({
            db,
            identity: reservationIdentity,
            teamId,
            mapId,
            matchId: activeReservation.matchId,
          });
          activeReservationRef.current = null;
        }
        const scaffold = await createVideoScaffold(resolved);
        if (firefoxRecording) firefoxCreatedVideoId = scaffold.videoIdToUse;
      } else if (options.matchIdToUse && options.videoIdToUse) {
        setMatchId(options.matchIdToUse);
        setVideoId(options.videoIdToUse);
      }

      const targetVideoConstraints = {
        width: { ideal: 1920, max: 1920 },
        height: { ideal: 1080, max: 1080 },
        frameRate: { ideal: 60, max: 60 },
      };

      const displayStream = firefoxRecording ? firefoxCaptureRef.current.stream : await navigator.mediaDevices.getDisplayMedia({
        video: targetVideoConstraints,
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });

      if (firefoxRecording) firefoxCaptureRef.current.assertActive();
      const displayVideoTrack = displayStream.getVideoTracks?.()[0] || null;
      if (displayVideoTrack) {
        try {
          displayVideoTrack.contentHint = "detail";
        } catch {}

        if (typeof displayVideoTrack.applyConstraints === "function") {
          try {
            await displayVideoTrack.applyConstraints(targetVideoConstraints);
          } catch (constraintError) {
            console.warn("Display track constraint refinement failed:", constraintError);
          }
        }
      }

      const captureSettings = displayVideoTrack?.getSettings?.() || {};
      captureQualityMetaRef.current = {
        captureWidth:
          Number.isFinite(captureSettings.width) && captureSettings.width > 0 ?
            Math.round(captureSettings.width) :
            null,
        captureHeight:
          Number.isFinite(captureSettings.height) && captureSettings.height > 0 ?
            Math.round(captureSettings.height) :
            null,
        captureFrameRate:
          Number.isFinite(captureSettings.frameRate) && captureSettings.frameRate > 0 ?
            Number(Number(captureSettings.frameRate).toFixed(2)) :
            null,
        recordingMimeType: bestMimeType || "video/webm",
        recordingMode: mode || "",
      };
      finalMediaMetaRef.current = {
        finalVideoWidth: null,
        finalVideoHeight: null,
        durationMs: null,
      };

      const mixed = await buildMixedStream(displayStream);
      mixedStreamRef.current = mixed;

      chunksRef.current = [];
      liveLobbySegmentIndexRef.current = 0;
      liveLobbyUploadChainRef.current = Promise.resolve();
      const rec = createOptimizedRecorder(mixed);
      mediaRecorderRef.current = rec;
      captureQualityMetaRef.current = {
        ...captureQualityMetaRef.current,
        recordingMimeType: rec.mimeType || bestMimeType || "video/webm",
      };

      rec.ondataavailable = (e) => {
        if (!e.data || e.data.size <= 0) return;

        const currentLobbyId =
          activeLiveLobbySession?.lobby?.id ||
          activeLiveLobbySession?.lobbyId ||
          liveLobbySessionRef.current?.lobby?.id ||
          liveLobbySessionRef.current?.lobbyId ||
          null;

        if (!currentLobbyId) {
          chunksRef.current.push(e.data);
          return;
        }

        const segmentIndex = liveLobbySegmentIndexRef.current;
        liveLobbySegmentIndexRef.current += 1;
        const startOffsetMs = segmentIndex * LIVE_LOBBY_SEGMENT_DURATION_MS;

        liveLobbyUploadChainRef.current = liveLobbyUploadChainRef.current
          .then(() =>
            uploadLiveLobbySegment({
              lobbyId: currentLobbyId,
              blob: e.data,
              segmentIndex,
              startOffsetMs,
            })
          )
          .catch((segmentError) => {
            console.error("[LiveRecordModal] Live lobby segment upload failed:", segmentError);
            showError(
              segmentError?.message ||
                "A live lobby segment failed to upload, so the session is ending."
            );
            liveLobbyStopReasonRef.current = "segment_upload_failed";
            stopRecording();
          });
      };

      rec.onstop = async () => {
        const currentLobbyId =
          activeLiveLobbySession?.lobby?.id ||
          activeLiveLobbySession?.lobbyId ||
          liveLobbySessionRef.current?.lobby?.id ||
          liveLobbySessionRef.current?.lobbyId ||
          null;

        if (currentLobbyId) {
          setIsRecording(false);
          setIsStopped(false);
          cleanupAudioGraph();

          try {
            await finalizeLiveLobbyRecording();
          } catch (finalizeError) {
            console.error("[LiveRecordModal] Failed to finalize live lobby:", finalizeError);
            showError(
              finalizeError?.message ||
                "The live lobby stopped, but finalization failed."
            );
          } finally {
            setUploading(false);
            setLiveLobbyFinalizing(false);
          }
          return;
        }

        const rawBlob = new Blob(chunksRef.current, { type: bestMimeType || "video/webm" });

        // Use actual elapsed capture time as the intended duration
        const recordedMs = Date.now() - startedAtMsRef.current;

        const fixedBlob = await indexRecordedWebm(await fixRecordedWebmDuration(rawBlob, recordedMs));

        const finalMediaMeta = await readBlobVideoMetadata(fixedBlob);
        finalMediaMetaRef.current = {
          finalVideoWidth: finalMediaMeta.finalVideoWidth,
          finalVideoHeight: finalMediaMeta.finalVideoHeight,
          durationMs:
            Number.isFinite(finalMediaMeta.durationMs) && finalMediaMeta.durationMs > 0 ?
              finalMediaMeta.durationMs :
              Math.max(0, Math.round(recordedMs)),
        };
        captureQualityMetaRef.current = {
          ...captureQualityMetaRef.current,
          recordingMimeType: fixedBlob?.type || rawBlob?.type || bestMimeType || "video/webm",
          recordingMode: mode || "",
        };

        setRecordedBlob(fixedBlob);

        const url = URL.createObjectURL(fixedBlob);
        setPreviewUrl(url);

        setIsStopped(true);
        setIsRecording(false);

        setSyncOffsetSec(null);
        setSyncMarked(false);
        setScoreboardCaptureSec(null);
        setScoreboardFrameFile(null);
        setOpenTeamStatsDialog(false);

        setTeamStatsDraft(null);
        setSavedTeamStatsMatchId(null);
        setSavedTeamStatsImagePath(null);

        cleanupAudioGraph();
      };

      startedAtMsRef.current = Date.now();
      setLiveNowVideoSec(0);
      setLiveNowMatchSec(0);

      setNoteTimeOverrideSec(null);
      setNoteTimeInput("");

      setRecordedBlob(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");

      if (firefoxRecording) {
        firefoxCaptureRef.current.assertActive();
        displayVideoTrack?.addEventListener('ended', stopRecording, {once: true});
      }
      rec.start(activeLiveLobbySession ? LIVE_LOBBY_SEGMENT_DURATION_MS : 500);
      setIsRecording(true);
      setIsStopped(false);
    } catch (e) {
      console.error(e);
      if (firefoxRecording) {firefoxCaptureRef.current?.stop(); firefoxCaptureRef.current = null;}
      if (firefoxRecording && firefoxCreatedVideoId) {
        await deleteDoc(doc(db, "teams", teamId, "maps", mapId, "videos", firefoxCreatedVideoId)).catch(() => {});
      }
      if (!options.liveLobbySession) {
        showError(e?.message || "Failed to start recording.");
      }
      cleanupAudioGraph();
      if (options.liveLobbySession) {
        throw e;
      }
    } finally {
      setIsPreparing(false);
    }
  }

  async function startLiveLobbyHost() {
    let sessionState = null;
    let createdVideoId = null;
    let firefoxCapture = null;
    try {
      if (!canUse) return showError("Missing team/map/auth context.");
      if (isPreparing || isRecording || uploading || creatingLiveLobby) return;

      setIsPreparing(true);
      setCreatingLiveLobby(true);
      setAlert(null);

      if (firefoxRecording) {
        if (focusPointsLoading) throw new Error("Wait for the event focus points to finish loading.");
        if (!setupReady) throw new Error("Save and select your event setup before recording.");
        firefoxCapture = prepareFirefoxCapture();
        await firefoxCapture.start();
        firefoxCapture.assertActive();
      }

      const resolved = await resolveMatchIdAndOptionalDoc();
      const { matchIdToUse, videoIdToUse } = await createVideoScaffold(resolved);
      createdVideoId = videoIdToUse;

      const response = await createLiveLobby({
        teamId,
        mapId,
        mapName: mapName || mapId,
        matchId: matchIdToUse,
        videoId: videoIdToUse,
        title: resolved.matchTitleToUse,
        povName,
        recordingMode: mode,
      });

      if (!response?.ok) {
        throw new Error("Failed to start the live lobby.");
      }

      sessionState = {
        ...response,
        hostMode: true,
      };
      setLiveLobbySession(sessionState);
      setShowLiveLobbyConfirm(false);

      await startRecording({
        skipScaffold: true,
        ...(firefoxRecording ? {firefoxCapture} : {}),
        matchIdToUse,
        videoIdToUse,
        liveLobbySession: sessionState,
      });
    } catch (error) {
      if (firefoxRecording) {firefoxCapture?.stop(); firefoxCaptureRef.current = null;}
      if (sessionState?.lobby?.id) {
        try {
          await endLiveLobby({
            teamId,
            lobbyId: sessionState.lobby.id,
            reason: "host_start_failed",
          });
        } catch (endError) {
          console.warn("Failed to close unfinished live lobby after start failure:", endError);
        }
      } else if (createdVideoId && teamId && mapId) {
        try {
          await deleteDoc(doc(db, "teams", teamId, "maps", mapId, "videos", createdVideoId));
        } catch (cleanupError) {
          console.warn("Failed to delete unfinished live-lobby scaffold video:", cleanupError);
        }
      }
      setLiveLobbySession(null);
      setMatchId(null);
      setVideoId(null);
      console.error("Failed to start live lobby:", error);
      showError(
        error?.message ||
          "Unable to start the live lobby right now."
      );
    } finally {
      setCreatingLiveLobby(false);
      setIsPreparing(false);
    }
  }

  async function captureVideoFrameFile(videoEl) {
    if (!videoEl) return null;

    if (videoEl.readyState < 2) {
      await new Promise((resolve) => {
        const handleLoadedData = () => {
          videoEl.removeEventListener("loadeddata", handleLoadedData);
          resolve();
        };
        videoEl.addEventListener("loadeddata", handleLoadedData);
      });
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const canvas = document.createElement("canvas");
    canvas.width = videoEl.videoWidth || 1920;
    canvas.height = videoEl.videoHeight || 1080;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((nextBlob) => resolve(nextBlob), "image/png");
    });

    if (!blob) return null;

    return new File([blob], `scoreboard-frame-${Date.now()}.png`, {
      type: "image/png",
    });
  }

  function parseTimeToSeconds(str) {
    const s = String(str || "").trim();
    if (!s) return NaN;

    // allow plain seconds (including decimals)
    if (!s.includes(":")) {
      const n = Number(s);
      return Number.isFinite(n) ? n : NaN;
    }

    // allow H:MM:SS or MM:SS
    const parts = s.split(":").map((p) => p.trim());
    if (parts.some((p) => p === "")) return NaN;

    // Support MM:SS(.d) or H:MM:SS(.d)
    let h = 0, m = 0, sec = 0;

    if (parts.length === 2) {
      m = Number(parts[0]);
      sec = Number(parts[1]);
    } else if (parts.length === 3) {
      h = Number(parts[0]);
      m = Number(parts[1]);
      sec = Number(parts[2]);
    } else {
      return NaN;
    }

    if (![h, m, sec].every(Number.isFinite)) return NaN;
    if (h < 0 || m < 0 || sec < 0) return NaN;

    return h * 3600 + m * 60 + sec;
  }

  async function handleSaveRecording() {
    if (uploading || isPreparing || isRecording) return;

    if (!recordedBlob) return showError("Record and stop the session before saving.");
    if (!syncMarked) return showError("Please mark the Sync Point first before saving.");
    await uploadRecording();
  }

  function sanitizeBaseName(name) {
    return String(name || "")
      .trim()
      .replace(/[^\w.-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  async function readBlobVideoMetadata(blob) {
    return await new Promise((resolve) => {
      if (!blob) {
        resolve({
          finalVideoWidth: null,
          finalVideoHeight: null,
          durationMs: null,
        });
        return;
      }

      const tempUrl = URL.createObjectURL(blob);
      const tempVideo = document.createElement("video");

      const finish = (payload) => {
        try {
          tempVideo.pause?.();
          tempVideo.removeAttribute("src");
          tempVideo.load?.();
        } catch {}
        URL.revokeObjectURL(tempUrl);
        resolve(payload);
      };

      tempVideo.preload = "metadata";
      tempVideo.muted = true;
      tempVideo.onloadedmetadata = () => {
        finish({
          finalVideoWidth:
            Number.isFinite(tempVideo.videoWidth) && tempVideo.videoWidth > 0 ?
              tempVideo.videoWidth :
              null,
          finalVideoHeight:
            Number.isFinite(tempVideo.videoHeight) && tempVideo.videoHeight > 0 ?
              tempVideo.videoHeight :
              null,
          durationMs:
            Number.isFinite(tempVideo.duration) && tempVideo.duration > 0 ?
              Math.round(tempVideo.duration * 1000) :
              null,
        });
      };
      tempVideo.onerror = () => {
        finish({
          finalVideoWidth: null,
          finalVideoHeight: null,
          durationMs: null,
        });
      };
      tempVideo.src = tempUrl;
    });
  }

  function buildRecordingStorageMetadata() {
    const captureMeta = captureQualityMetaRef.current || {};
    const finalMeta = finalMediaMetaRef.current || {};
    const metadata = {};

    const addValue = (key, value) => {
      if (value == null || value === "") return;
      metadata[key] = String(value);
    };

    addValue("captureWidth", captureMeta.captureWidth);
    addValue("captureHeight", captureMeta.captureHeight);
    addValue("captureFrameRate", captureMeta.captureFrameRate);
    addValue("recordingMimeType", captureMeta.recordingMimeType);
    addValue("recordingMode", captureMeta.recordingMode);
    addValue("finalVideoWidth", finalMeta.finalVideoWidth);
    addValue("finalVideoHeight", finalMeta.finalVideoHeight);
    addValue("durationMs", finalMeta.durationMs);

    return metadata;
  }

  async function uploadLiveLobbySegment({
    lobbyId,
    blob,
    segmentIndex,
    startOffsetMs,
  }) {
    if (!teamId || !mapId || !videoId || !auth.currentUser?.uid) {
      throw new Error("Missing live lobby upload context.");
    }

    const storage = getStorage();
    // Live segments upload before the final recording upload prepares its claim.
    await ensureActiveTeamClaim(teamId);
    const safeSegmentName = `segment-${String(segmentIndex).padStart(6, "0")}.webm`;
    const objectPath = `videos/${teamId}/${mapId}/${videoId}/live-lobby/${lobbyId}/segments/${safeSegmentName}`;
    const objectRef = storageRef(storage, objectPath);
    const contentType = blob?.type || mediaRecorderRef.current?.mimeType || bestMimeType || "video/webm";

    const uploadTask = uploadBytesResumable(objectRef, blob, {
      contentType,
      cacheControl: "private, max-age=60",
      customMetadata: {
        teamId,
        mapId,
        videoDocId: videoId,
        createdBy: username || "",
        createdByUid: auth.currentUser.uid || "",
        matchId: matchId || "",
        povName: povName || "",
        mode: mode || "",
        liveLobbyChunk: "true",
        liveLobbyId: lobbyId,
        segmentIndex: String(segmentIndex),
      },
    });

    await new Promise((resolve, reject) => {
      uploadTask.on("state_changed", undefined, reject, resolve);
    });

    const videoURL = await getDownloadURL(objectRef);
    const response = await commitLiveLobbySegment({
      teamId,
      lobbyId,
      segmentId: safeSegmentName,
      segmentIndex,
      objectPath,
      videoURL,
      mimeType: contentType,
      sizeBytes: Number(uploadTask.snapshot.totalBytes || blob.size || 0),
      durationMs: LIVE_LOBBY_SEGMENT_DURATION_MS,
      startOffsetMs,
    });

    if (!response?.ok) {
      throw new Error("Unable to commit the live lobby segment.");
    }

    if (response?.lobby) {
      setLiveLobbySession((current) =>
        current ? { ...current, lobby: response.lobby } : { lobby: response.lobby }
      );
    }

    if (response?.shouldTerminate) {
      liveLobbyStopReasonRef.current = "quota_shutdown";
      stopRecording();
    }

    triggerTeamQuotaRefresh({
      teamId,
      bytesDelta: Number(uploadTask.snapshot.totalBytes || blob.size || 0),
    });
  }

  async function finalizeLiveLobbyRecording() {
    const currentLobbyId = liveLobbySessionRef.current?.lobby?.id || liveLobbySessionRef.current?.lobbyId || null;
    if (!teamId || !currentLobbyId) return;

    setLiveLobbyFinalizing(true);
    setUploading(true);
    setUploadProgress(100);

    await liveLobbyUploadChainRef.current.catch((error) => {
      console.warn("[LiveRecordModal] Live lobby upload queue failed before finalization:", error);
    });

    const response = await endLiveLobby({
      teamId,
      lobbyId: currentLobbyId,
      reason: liveLobbyStopReasonRef.current || "manual_end",
    });

    if (response?.lobby) {
      setLiveLobbySession((current) =>
        current ? { ...current, lobby: response.lobby } : { lobby: response.lobby }
      );
    }

    onVideoUploaded?.();
    onClose?.();
  }

  async function uploadRecording() {
    try {
      if (!recordedBlob) return showError("No recording found.");
      if (!matchId || !videoId) return showError("Missing match/video IDs.");

      if (!syncMarked || syncOffsetSec === null) {
        return showError("Please mark the sync point in the preview before saving.");
      }

      setUploading(true);
      setUploadProgress(0);
      setAlert(null);

      await ensureActiveTeamClaim(teamId);
      await auth.currentUser.getIdToken(true);

      const storage = getStorage();

      /**
       * ✅ Match UploadVideoModal storage layout + naming convention
       * so Storage accounting / Team Storage updates identically.
       */
      const matchTitleForName =
        matchMode === "new"
          ? newMatchTitle
          : (matches.find((m) => m.id === selectedMatchId)?.title || "Match");

      const base = sanitizeBaseName(
        `${matchTitleForName || "Live-Recording"}-${povName || "POV"}-${videoId}`
      );

      const safeName = base.toLowerCase().endsWith(".webm") ? base : `${base}.webm`;

      const finalPath = `videos/${teamId}/${mapId}/${videoId}/${safeName}`;
      const storageRefObj = storageRef(storage, finalPath);

      const allowance = await checkTeamQuotaAllowance({
        teamId,
        action: "upload_video",
        bytesToAdd: recordedBlob.size || 0,
      });

      if (!allowance?.uploadAllowed) {
        throw new Error(allowance?.message || "Uploading is unavailable right now.");
      }

      const uploadTask = uploadBytesResumable(storageRefObj, recordedBlob, {
        contentType: recordedBlob?.type || bestMimeType || "video/webm",
        cacheControl: "public, max-age=31536000, immutable",
        contentDisposition: `inline; filename="${safeName}"`,
        customMetadata: {
          teamId,
          mapId,
          videoDocId: videoId,
          createdBy: username || "",
          createdByUid: auth.currentUser.uid || "",
          matchId: matchId || "",
          povName: povName || "",
          mode: mode || "",
          ...buildRecordingStorageMetadata(),
        },
      });

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snap) => {
            const p = (snap.bytesTransferred / snap.totalBytes) * 100;
            setUploadProgress(Number(p.toFixed(1)));
          },
          reject,
          () => resolve()
        );
      });

      const finalization = await confirmTrackedUploadFinalize({
        teamId,
        objectPath: finalPath,
      });

      if (!finalization?.accepted) {
        throw new Error(
          finalization?.message || "Upload was rejected by the current team quota rules."
        );
      }

      const videoDocRef = doc(db, "teams", teamId, "maps", mapId, "videos", videoId);
      await updateDoc(videoDocRef, {
        storagePath: finalPath,
        sizeBytes: uploadTask.snapshot.totalBytes ?? null,
        sourceBytes: uploadTask.snapshot.totalBytes ?? null,
        status: "ready",
        syncOffsetSec: Number(Number(syncOffsetSec).toFixed(3)),
      });

      triggerTeamQuotaRefresh({
        teamId,
        bytesDelta: Number(uploadTask.snapshot.totalBytes || recordedBlob?.size || 0),
      });
      onVideoUploaded?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      showError(e?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function deleteLiveRecordingAndAllData() {
    // An unopened setup has no recording to delete. In particular, closing it
    // must not require a selected map or delete an existing event's match.
    if (!videoId && !recordedBlob && !savedTeamStatsMatchId) {
      onClose?.();
      return;
    }
    if (!teamId || !mapId) {
      showError("Missing team/map context.");
      return;
    }

    try {
      setUploading(true);
      await ensureActiveTeamClaim(teamId);
      await auth.currentUser?.getIdToken?.(true);
      setAlert(null);

      const storage = getStorage();

      let deletedBytes = 0;

      // 1) delete storage object (if uploaded) + video doc
      if (videoId) {
        const vRef = doc(db, "teams", teamId, "maps", mapId, "videos", videoId);
        const vSnap = await getDoc(vRef);

        if (vSnap.exists()) {
          const vData = vSnap.data() || {};
          deletedBytes = Number(vData?.sizeBytes || vData?.sourceBytes || 0);
          if (vData.storagePath) {
            await deleteObject(storageRef(storage, vData.storagePath));
          }
        }

        await deleteDoc(vRef);
      }

      // Shared match data belongs to every POV, not just this recording.
      let mayDeleteSharedMatch = false;
      if (matchId && videoId) {
        const vidsRef = collection(db, "teams", teamId, "maps", mapId, "videos");
        const vidsSnap = await getDocs(query(vidsRef, where("matchId", "==", matchId)));
        mayDeleteSharedMatch = vidsSnap.docs.every((entry) => entry.id === videoId);
      }

      // 2) Clean shared notes and media only after confirming this was the last POV.
      if (matchId && mayDeleteSharedMatch) {
        const notesRef = collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "notes");
        
        // delete note images in storage first
        try {
          const notesSnap = await getDocs(notesRef);
          const paths = notesSnap.docs
            .map((d) => d.data()?.mediaStoragePath)
            .filter(Boolean);

          // delete sequentially (safer), or Promise.allSettled if you prefer
          for (const p of paths) {
            try {
              await deleteObject(storageRef(storage, p));
            } catch (e) {
              console.warn("Failed to delete note image:", p, e?.message || e);
            }
          }
        } catch (e) {
          console.warn("Note image cleanup failed:", e?.message || e);
        }
        
        const repliesRef = collection(
          db,
          "teams",
          teamId,
          "maps",
          mapId,
          "matches",
          matchId,
          "allReplies"
        );

        const deleteAllInChunks = async (colRef) => {
          const snap = await getDocs(colRef);
          let batch = writeBatch(db);
          let n = 0;

          for (const d of snap.docs) {
            batch.delete(d.ref);
            n++;
            if (n >= 450) {
              await batch.commit();
              batch = writeBatch(db);
              n = 0;
            }
          }
          if (n) await batch.commit();
        };

        await deleteAllInChunks(notesRef).catch(() => {});
        await deleteAllInChunks(repliesRef).catch(() => {});

        // 3) optional: delete match doc if no other videos exist for this match
        if (matchId) {
          const vidsRef = collection(db, "teams", teamId, "maps", mapId, "videos");
          const qRef = query(vidsRef, where("matchId", "==", matchId));
          const vidsSnap = await getDocs(qRef);

          const otherVids = videoId
            ? vidsSnap.docs.filter((d) => d.id !== videoId)
            : vidsSnap.docs;

          if (otherVids.length === 0) {
            // ✅ delete focus points ONLY if this is the last POV/video for the match
            const focusPointsRef = collection(
              db,
              "teams",
              teamId,
              "maps",
              mapId,
              "matches",
              matchId,
              "focusPoints"
            );
            await deleteAllInChunks(focusPointsRef).catch(() => {});

            // ✅ delete match doc
            await deleteDoc(doc(db, "teams", teamId, "maps", mapId, "matches", matchId)).catch(() => {});
          }
        }
      }

      // 4) delete saved team-stats doc + screenshot if they were created
      await cleanupSavedTeamStatsForLiveRecording();

      if (deletedBytes > 0) {
        triggerTeamQuotaRefresh({
          teamId,
          bytesDelta: -deletedBytes,
        });
      }

      setUploading(false);
      onClose?.();
    } catch (e) {
      console.error("Delete live recording failed:", e);
      setUploading(false);
      showError("Delete failed. Check console for details.");
    }
  }

  // ✅ Audio test (monitor mixed audio)
  async function toggleAudioTest() {
    const dest = audioGraphRef.current.dest;
    if (!dest) {
      showError("Start recording first, then use Test Audio (browser needs an active capture/mix).");
      return;
    }

    try {
      // ✅ Ensure AudioContext is actually running
      const ctx = audioGraphRef.current.ctx;
      if (ctx && ctx.state === "suspended") {
        await ctx.resume();
      }

      const el = monitorAudioRef.current;
      if (!el) return;

      if (isMonitoring) {
        el.pause?.();
        el.srcObject = null;
        setIsMonitoring(false);
        return;
      }

      el.autoplay = true;
      el.muted = false;       // (default, but explicit)
      el.volume = 1;          // you can expose this later if you want

      el.srcObject = dest.stream;
      await el.play();
      setIsMonitoring(true);
    } catch (e) {
      console.warn("Audio test failed:", e);
      showError("Audio test failed (browser blocked playback). Click the Test button again.");
    }
  }

  // Coach: pass current match time to notes, but allow override.
  const coachMatchTimeSec = useMemo(() => {
    if (isRecording) return liveNowMatchSec;

    const pv = previewVideoRef.current;
    if (pv && pv.duration) {
      const videoT = pv.currentTime || 0;
      const off = Number.isFinite(Number(syncOffsetSec)) ? Number(syncOffsetSec) : 0;
      return Math.max(0, toMatchTimeSec(videoT, off));
    }
    return 0;
  }, [isRecording, liveNowMatchSec, syncOffsetSec]);

  useEffect(() => {
    if (noteTimeOverrideSec === null) {
      // ✅ show hh:mm:ss while live
      setNoteTimeInput(formatTimeHMS(coachMatchTimeSec));
    }
  }, [coachMatchTimeSec, noteTimeOverrideSec]);

  // useEffect(() => {
  //   const handler = (e) => {
  //     // Only log clicks that happen while recording (your problem state)
  //     if (!isRecording) return;

  //     const el = document.elementFromPoint(e.clientX, e.clientY);
  //     if (!el) return;

  //     // Log the “topmost” element at the click point
  //     console.log(
  //       "[CLICK TRACE] top element:",
  //       el,
  //       "class=",
  //       el.className,
  //       "id=",
  //       el.id
  //     );
  //   };

  //   // Capture phase so we can see it even if something stops propagation
  //   document.addEventListener("pointerdown", handler, true);

  //   return () => document.removeEventListener("pointerdown", handler, true);
  // }, [isRecording]);

  useLiveSetupFocusPoints({teamId, mapId, matchId: focusPointTargetMatchId,
    eventId: selectedCalendarEventId, event: selectedCalendarEvent,
    selectionKey: `${matchMode}:${selectedMatchId}`, disabled: isRecording || uploading || isPreparing,
    setFocusPoints, setLoading: setFocusPointsLoading,
    onError: error => {
      console.error("Could not update event focus points:", error);
      setAlert({type: "error", message: "Could not update event focus points. Check your connection and select the event again to retry."});
    },
  });

  const freezeNoteTime = () => {
    const v = Number(coachMatchTimeSec.toFixed(1));
    setNoteTimeOverrideSec(v);
    setNoteTimeInput(formatTimeHMS(v));
  };

  const goLiveNoteTime = () => {
    setNoteTimeOverrideSec(null);
  };

  const chosenNoteTimeSec = Number.isFinite(noteTimeOverrideSec)
    ? Number(noteTimeOverrideSec)
    : Number(coachMatchTimeSec.toFixed(1));

  const behindLiveSec = isRecording
    ? Math.max(0, Number(liveNowMatchSec || 0) - Number(chosenNoteTimeSec || 0))
    : 0;

  const stableLinkedVideoMeta = useMemo(() => {
    return {
      mapId,
      videoId,
      matchId,
      scoreboardCaptureSec,
      syncOffsetSec,
      comp: selectedComp.filter(Boolean),
    };
  }, [mapId, videoId, matchId, scoreboardCaptureSec, syncOffsetSec, selectedComp]);

  const requestClose=()=>{
    if(!videoId&&!recordedBlob&&!savedTeamStatsMatchId)onClose?.();
    else setShowDeleteConfirm(true);
  };

  return (
    <>
      {alert && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          position="top"
          onClose={() => setAlert(null)}
        />
      )}

      {/* Delete confirmation (must be above live modal via z-index CSS fix below) */}
      {showDeleteConfirm && (
        <DeleteHighlightConfirm
          message="This will delete the recording and ALL associated notes/data for this live session. This cannot be undone."
          onConfirm={async () => {
            setShowDeleteConfirm(false);
            await deleteLiveRecordingAndAllData();
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      <div className="live-record-modal" role="dialog" aria-modal="true" aria-label="Live record">
        <div className="lrm-content">
          <div className="lrm-header">
            <h3>Live Record</h3>

            {/* X triggers delete confirm */}
            <button
              className="lrm-close app-cancel-control"
              onClick={requestClose}
              disabled={isPreparing || isRecording || uploading}
              title={videoId||recordedBlob?'Discard recording':'Close recording setup'}
              aria-label="Close recording setup"
            >
              ×
            </button>
          </div>

          <div className="lrm-body">
            {/* Mode */}
            <div className="lrm-row">
              <div className="lrm-mode">
                <button
                  type="button"
                  className={mode === "player" ? "lrm-btn selected" : "lrm-btn"}
                  onClick={() => setMode("player")}
                  disabled={isRecording || uploading}
                >
                  Player Recording
                </button>
                <button
                  type="button"
                  className={mode === "coach" ? "lrm-btn selected" : "lrm-btn"}
                  onClick={() => setMode("coach")}
                  disabled={isRecording || uploading}
                >
                  Coach Recording
                </button>
              </div>
            </div>

            {/* Match setup */}
            <div className="lrm-block">
              <p className="lrm-title">Match</p>
              <CalendarMatchLinker
                lifecycle={setupLifecycle}
                teamId={teamId}
                mapId={mapId}
                mapName={mapName}
                onReadyChange={setSetupReady}
                selectedEventId={selectedCalendarEventId}
                onSelectEvent={handleCalendarEventSelection}
                disabled={isRecording || uploading || isPreparing}
              />
            </div>

            {/* POV */}
            <div className="lrm-block">
              <p className="lrm-title">POV Name</p>
              <select
                className="lrm-select"
                value={povName}
                onChange={(e) => setPovName(e.target.value)}
                disabled={isRecording || uploading}
              >
                {povOptions.map((p) => (
                  <option key={p} value={p}>
                    {buildCloudPovOptionLabel(p)}
                  </option>
                ))}
              </select>
            </div>

            <div className="lrm-block recording-preparation-grid">
              <div>
                <FocusPoints
                  mapId={mapId}
                  teamId={teamId}
                  matchId={focusPointTargetMatchId}
                  videoId={videoId || ""}
                  focusPoints={focusPoints}
                  onDraftChange={focusPointTargetMatchId ? undefined : setFocusPoints}
                  alwaysEditable
                  instantDraft={!focusPointTargetMatchId}
                  instantSave={!!focusPointTargetMatchId}
                  returnToLibrary
                  showEmptyPlaceholder
                  showRatings={false}
                  disabled={uploading}
                />
              </div>
            </div>

            
            

            {/* Audio options */}
            <div className="lrm-block">
              <p className="lrm-title">Audio</p>

              <label className="lrm-check">
                <input
                  type="checkbox"
                  checked={includeMic}
                  onChange={(e) => {
                    if (uiLocked) return;
                    setIncludeMic(e.target.checked);
                  }}
                  disabled={uploading}
                />
                Include microphone
              </label>

              {includeMic && (
                <select
                  className="lrm-select"
                  value={micDeviceId}
                  onChange={(e) => {
                    if (uiLocked) return;
                    setMicDeviceId(e.target.value);
                  }}
                  disabled={uploading}
                >
                  <option value="default">Default mic</option>
                  {devices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Mic ${d.deviceId.slice(0, 6)}`}
                    </option>
                  ))}
                </select>
              )}

              <p className="lrm-hint">
                {firefoxRecording ? <>
                  <strong>For the best and easiest recording experience, use Google Chrome or another Chromium-based browser, such as Microsoft Edge.</strong>{" "}
                  When sharing your screen, enable “Share system audio” if offered. Availability depends on your browser and operating system.
                  <br/><br/>
                  <strong>Staying in Firefox or Zen?</strong>{" "}
                  These browsers cannot capture desktop audio directly through screen sharing. First enable or install a loopback input, such as Stereo Mix or a virtual audio cable, in your computer’s audio settings.
                  Route all audio you want recorded—including Valorant and Discord—to that input, then select the actual device in the dropdown below.
                  <br/><br/>
                  Leaving the dropdown at “none selected” adds no desktop audio through this input. Your microphone is controlled separately above.
                </> : <>System audio depends on the OS/browser and what you choose in the picker.
                Extra sources (like Discord) only work if they appear as an input device (e.g., virtual cable).</>}
              </p>

              <select
                className="lrm-select"
                value={extraAudioDeviceId}
                onChange={(e) => {
                  if (uiLocked) return;
                  setExtraAudioDeviceId(e.target.value);
                }}
                disabled={uploading}
              >
                <option value="">{firefoxRecording ? "Select a loopback device (none selected)" : "(Optional) Extra audio input device"}</option>
                {devices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Input ${d.deviceId.slice(0, 6)}`}
                  </option>
                ))}
              </select>

              {/* Mute toggles */}
              <div className="lrm-audio-mutes">
                <label className="lrm-mute-row">
                  <input
                    type="checkbox"
                    checked={muteSystem}
                    onChange={(e) => setMuteSystem(e.target.checked)}
                    disabled={uploading || firefoxRecording}
                  />
                  {firefoxRecording ? "Screen-share audio unavailable" : "Mute System Audio"}
                </label>

                <label className="lrm-mute-row">
                  <input
                    type="checkbox"
                    checked={muteMic}
                    onChange={(e) => setMuteMic(e.target.checked)}
                    disabled={!includeMic || uploading}
                  />
                  Mute Mic
                </label>

                <label className="lrm-mute-row">
                  <input
                    type="checkbox"
                    checked={muteExtra}
                    onChange={(e) => setMuteExtra(e.target.checked)}
                    disabled={!extraAudioDeviceId || uploading}
                  />
                  {firefoxRecording ? "Mute Game/System Input" : "Mute Extra Input"}
                </label>
              </div>

              {/* ✅ Volume sliders */}
              <div className="lrm-audio-mutes">
                <div className="lrm-mute-row">
                  <span>{firefoxRecording ? "Screen-share audio unavailable" : "System Volume"}</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={systemVolume}
                    onChange={(e) => setSystemVolume(Number(e.target.value))}
                    disabled={uploading || firefoxRecording}
                    style={{ flex: 1 }}
                  />
                  <span>{Math.round(systemVolume * 100)}%</span>
                </div>

                <div className="lrm-mute-row">
                  <span>Mic Volume</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={micVolume}
                    onChange={(e) => setMicVolume(Number(e.target.value))}
                    disabled={!includeMic || uploading}
                    style={{ flex: 1 }}
                  />
                  <span>{Math.round(micVolume * 100)}%</span>
                </div>

                <div className="lrm-mute-row">
                  <span>{firefoxRecording ? "Game/System Input Volume" : "Extra Volume"}</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={extraVolume}
                    onChange={(e) => setExtraVolume(Number(e.target.value))}
                    disabled={!extraAudioDeviceId || uploading}
                    style={{ flex: 1 }}
                  />
                  <span>{Math.round(extraVolume * 100)}%</span>
                </div>

                <div className="lrm-mute-row">
                  <button
                    type="button"
                    className="lrm-btn"
                    onClick={toggleAudioTest}
                    disabled={uploading}
                    title="Starts monitoring the mixed audio while recording (may echo if you monitor speakers)."
                  >
                    {isMonitoring ? "Stop Audio Test" : "Test Audio"}
                  </button>

                  {/* Hidden audio element used for monitoring */}
                  <audio ref={monitorAudioRef} />
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="lrm-controls">
              {!isRecording && !isStopped && (
                <button className="lrm-primary" onClick={() => startRecording()} disabled={isPreparing || uploading}>
                  {isPreparing ? "Preparing…" : "Start Recording"}
                </button>
              )}

              {isRecording && (
                <>
                  <div className="lrm-live">
                    <span className="lrm-dot" />
                    <span className="lrm-live-label">Recording —</span>
                    <span className="lrm-live-time">{formatTimeHMS(liveNowVideoSec)}</span>
                  </div>

                  <button className="lrm-danger" onClick={stopRecording}>
                    Stop
                  </button>
                </>
              )}
            </div>

            {(liveLobbySession || liveLobbyFinalizing) && (
              <div className="lrm-live-lobby-confirm">
                <div className="lrm-live-lobby-confirm__copy">
                  <strong>{liveLobbyFinalizing ? "Finalizing Live Lobby" : "Live Lobby Active"}</strong>
                  <p>
                    {liveLobbyFinalizing
                      ? "The final uploaded segments are being committed and the session is being finalized into a reviewable VOD."
                      : `This session is uploading in near-live ${(LIVE_LOBBY_SEGMENT_DURATION_MS / 1000).toFixed(0)} second segments. Teammates can join from the VOD reviewer and watch with a short delay.`}
                  </p>
                  {liveLobbySession?.lobby?.status === "quota_shutdown_grace" ? (
                    <p>
                      <strong>{getLiveLobbyShutdownLabel(liveLobbySession?.lobby?.quotaShutdownReason)}</strong>
                      {" "}The lobby will self-terminate once the grace countdown expires.
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            {!isRecording && !isStopped && (
              <div className="lrm-live-lobby-launch">
                <button
                  className="lrm-btn"
                  type="button"
                  onClick={() => setShowLiveLobbyConfirm((current) => !current)}
                  disabled={isPreparing || uploading || creatingLiveLobby}
                  title="Start a true live lobby with live viewing and shared live notes."
                >
                  {creatingLiveLobby ? "Starting Live Lobby..." : "Start Live Lobby"}
                </button>
              </div>
            )}

            {showLiveLobbyConfirm && !isRecording && !isStopped && (
              <div className="lrm-live-lobby-confirm">
                <div className="lrm-live-lobby-confirm__copy">
                  <strong>Open Live Lobby?</strong>
                  <p>
                    Same-team members with active VOD reviewer access will be able to join this session, watch it live, and add shared live notes.
                  </p>
                  <p>
                    Live Lobby mode starts counting team storage and monthly upload quota during the session itself. If a quota is reached, everyone will be warned and the lobby will self-terminate after a 30 second countdown.
                  </p>
                  <p>Live Lobby quality is capped at 1080p60 max.</p>
                </div>

                <div className="lrm-live-lobby-confirm__actions">
                  <button
                    type="button"
                    className="lrm-primary"
                    onClick={startLiveLobbyHost}
                    disabled={creatingLiveLobby || isPreparing || uploading}
                  >
                    {creatingLiveLobby ? "Starting..." : "Confirm and Start Live Lobby"}
                  </button>
                  <button
                    type="button"
                    className="lrm-btn app-cancel-control"
                    onClick={() => setShowLiveLobbyConfirm(false)}
                    disabled={creatingLiveLobby}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Preview + sync marker after stop */}
            {isStopped && previewUrl && (
              <VodMarkerPicker src={previewUrl} videoRef={previewVideoRef} syncOffsetSec={syncOffsetSec} scoreboardCaptureSec={scoreboardCaptureSec} disabled={uploading} onMarkSync={seconds=>{setSyncOffsetSec(seconds);setSyncMarked(true);}} onCaptureStats={openTeamStatsFromPreview} hasStats={!!scoreboardFrameFile} onReopenStats={()=>void openTeamStatsFromPreview()}/>
            )}
            {/* Coach overlay: live notes */}
            {mode === "coach" && matchId && (
              <div className="lrm-coach">
                <div className="lrm-coach-head">
                  <div className="lrm-coach-title">Coach Notes</div>

                  <div className="lrm-time-override">
                    <label>Note time (match):</label>

                    <input
                      className="lrm-input lrm-time-input"
                      type="text"
                      inputMode="decimal"
                      value={noteTimeInput}
                      onFocus={() => {
                        if (noteTimeOverrideSec === null) {
                          const v = Number(coachMatchTimeSec.toFixed(1));
                          setNoteTimeOverrideSec(v);
                          setNoteTimeInput(formatTimeHMS(v));
                        }
                      }}
                      onChange={(e) => {
                        const next = e.target.value;
                        setNoteTimeInput(next);

                        if (noteTimeOverrideSec === null) {
                          setNoteTimeOverrideSec(Number(coachMatchTimeSec.toFixed(1)));
                        }

                        const parsed = parseTimeToSeconds(next);
                        if (Number.isFinite(parsed)) {
                          setNoteTimeOverrideSec(parsed);
                        }
                      }}
                      disabled={uploading}
                    />

                    <button
                      type="button"
                      className="lrm-btn"
                      onClick={freezeNoteTime}
                      disabled={uploading}
                      title="Freeze timestamp at the current match time"
                    >
                      Freeze
                    </button>

                    <button
                      type="button"
                      className="lrm-btn"
                      onClick={goLiveNoteTime}
                      disabled={uploading}
                      title="Return to live match time"
                    >
                      Live
                    </button>

                    {isRecording && (
                      <span className="lrm-time-behind">(−{behindLiveSec.toFixed(1)}s)</span>
                    )}
                  </div>
                </div>

                <div className="lrm-coach-grid">
                  <div className="lrm-coach-input">
                    <NoteInputBox
                      mapId={mapId}
                      matchId={matchId}
                      matchTimeSec={
                        noteTimeOverrideSec === null
                          ? Number(coachMatchTimeSec.toFixed(1))
                          : noteTimeOverrideSec
                      }
                      onHide={() => {}}
                      onBeginTyping={() => {
                        // Freeze the note timer the moment the coach starts typing (only if live)
                        if (noteTimeOverrideSec === null) {
                          freezeNoteTime();
                        }
                      }}
                      onPosted={() => {
                        // After posting, return to live time immediately
                        goLiveNoteTime();
                      }}
                    />
                  </div>

                  <div className="lrm-coach-notes">
                    <NotesDisplay
                      mapId={mapId}
                      matchId={matchId}
                      syncOffsetSec={Number.isFinite(Number(syncOffsetSec)) ? Number(syncOffsetSec) : 0}
                      currentTime={0}
                      onSeek={(videoTimeSec) => {
                        const v = previewVideoRef.current;
                        if (!v) return;
                        v.currentTime = videoTimeSec;
                        v.play?.().catch(() => {});
                      }}
                      displayMode={"list"}
                      isPlaying={false}
                      autoScroll={false}
                      onDisableAuto={() => {}}
                      filters={{
                        filterMentions: [],
                        includeTeam: false,
                        filterImportance: [],
                        filterTags: [],
                        hideReviewedByMe: false,
                        currentUser: username,
                      }}

                      autoScrollToNewest={true}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer is now Save + Delete */}
          <div className="lrm-footer">
            {liveLobbySession || liveLobbyFinalizing ? (
              <span className="lrm-sync-readout">
                Live Lobby recordings finalize automatically when you stop the session.
              </span>
            ) : (
              <button
                className="lrm-btn lrm-btn-save"
                onClick={handleSaveRecording}
                disabled={isPreparing || isRecording || uploading || !recordedBlob || !syncMarked}
                title={!recordedBlob ? "Record first" : !syncMarked ? "Mark sync first" : "Save"}
              >
                {uploading ? `Saving… ${uploadProgress}%` : "Save"}
              </button>
            )}

            <button
              className="lrm-btn lrm-btn-danger"
              type="button"
              onClick={requestClose}
              disabled={isPreparing || isRecording || uploading}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
      {openTeamStatsDialog && scoreboardFrameFile && (
        <UploadScoreDialog
          initialSetupEventId={selectedCalendarEventId}
          savedStatsId={savedTeamStatsMatchId}
          key={`${videoId || "pending-video"}-${scoreboardCaptureSec || "no-time"}`}
          teamId={teamId}
          onClose={() => {
            // Only close the dialog UI.
            // Keep the draft/frame/capture values for reopening.
            resetTeamStatsModalUiOnly();
          }}
          hideFileInput={true}
          initialImageBlob={scoreboardFrameFile}
          referenceVideoUrl={previewUrl}
          initialMatchDateISO={todayISO}
          disableImageUpload={true}
          draftState={teamStatsDraft}
          onDraftChange={setTeamStatsDraft}
          linkedVideoMeta={stableLinkedVideoMeta}
          onSaved={(savedMatch) => {
            setSavedTeamStatsMatchId(savedMatch?.id || null);
            setSavedTeamStatsImagePath(savedMatch?.imageStoragePath || null);
            setOpenTeamStatsDialog(false);
            setAlert({
              message: "Team stats saved from live recording frame and linked automatically.",
              type: "success",
            });
          }}
        />
      )}
    </>
  );
}
